import { useRef, useState } from 'react';

import { TAgent, TRunAgentParams } from '@/types/agents';

export enum SidebarStage {
  Details = 'details',
  Configure = 'configure',
  Input = 'input',
}

interface UseAgentDetailsSidebarProps {
  agent: TAgent | null;
  onRunAgent: (params: TRunAgentParams) => void;
  onClose: () => void;
}

const useAgentDetailsSidebar = ({
  agent,
  onRunAgent,
  onClose,
}: UseAgentDetailsSidebarProps) => {
  const [stage, setStage] = useState<SidebarStage>(SidebarStage.Details);
  const [configId, setConfigId] = useState('');
  const [selectedCredentials, setSelectedCredentials] = useState<Record<string, string>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const filesRef = useRef<Record<string, File | File[]>>({});

  const handleStageChange = (newStage: SidebarStage) => {
    setStage(newStage);
  };

  const handleCredentialChange = (credentialSlotKey: string, value: string) => {
    setSelectedCredentials((prev) => ({ ...prev, [credentialSlotKey]: value }));
  };

  const handleConfigContinue = () => {
    handleStageChange(SidebarStage.Input);
  };

  const handleDrawerClose = () => {
    setConfigId('');
    setSelectedCredentials({});
    setInputValues({});
    filesRef.current = {};
    setStage(SidebarStage.Details);
    onClose();
  };

  const handleRunTest = () => {
    if (!agent) {
      return;
    }

    const properties = Object.entries(inputValues).map(([key, value]) => {
      const schemaField = agent.schema.input[key];

      if (schemaField?.type === 'files') {
        const ids = value.split(',').map((id) => id.trim()).filter(Boolean);

        return {
          propertyName: key,
          value: ids,
        };
      }

      return {
        propertyName: key,
        value: value,
      };
    });

    onRunAgent({
      agent,
      properties,
      configId,
    });
  };

  const handleInputValueChange = (key: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (key: string, file: File | File[] | null) => {
    if (file) {
      filesRef.current[key] = file;
    } else {
      delete filesRef.current[key];
      setInputValues((prev) => {
        const newValues = { ...prev };
        delete newValues[key];

        return newValues;
      });
    }
  };

  return {
    stage,
    setStage,
    configId,
    setConfigId,
    selectedCredentials,
    setSelectedCredentials,
    inputValues,
    filesRef,
    handleInputValueChange,
    handleFileChange,
    handleStageChange,
    handleCredentialChange,
    handleConfigContinue,
    handleDrawerClose,
    handleRunTest,
  };
};

export default useAgentDetailsSidebar;